import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable max-lines */
import * as React from 'react';
import { clsx } from 'clsx';
import { useUiKitToastApiContext } from '../../context';
import { IconActionClose16, IconActionClose24, IconFunctionalWarningCircle24, IconFunctionalSuccessCircle24 } from '@cian/ui-kit-design-tokens/icons';
import { ArticleParagraph1, UIText2 } from '../../../typography';
import { Spinner } from '../../../loader/components/Spinner';
import { useGetIsMobileDevice } from './internal/hooks';
import { LeftAdornment } from './internal/components/LeftAdornment';
import { RightAdornment } from './internal/components/RightAdornment';
import { ProgressAdornment } from './internal/components/ProgressAdornment';
import { ProgressBar } from './internal/components/ProgressBar';
import * as styles from './Toast.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var ToastComponent = function ToastComponent(props) {
  var ref = React.useRef(null);
  var toastsApiContext = useUiKitToastApiContext();
  var _React$useState = React.useState(props.timerId),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    currentHideTimerId = _React$useState2[0],
    setCurrentHideTimerId = _React$useState2[1];
  var isMobileDevice = useGetIsMobileDevice();
  var type = props.type,
    size = props.size,
    placement = props.placement,
    text = props.text,
    hidden = props.hidden,
    hideDelay = props.hideDelay,
    isHidable = props.isHidable,
    contentRenderFunctionOrJSX = props.content,
    leftAdornmentRenderFunctionOrJSX = props.leftAdornment,
    rightAdornmentRenderFunctionOrJSX = props.rightAdornment,
    id = props.id,
    onClose = props.onClose,
    hasCloseIcon = props.hasCloseIcon,
    confirmAction = props.confirmAction,
    dismissAction = props.dismissAction,
    _props$confirmText = props.confirmText,
    confirmText = _props$confirmText === void 0 ? 'Посмотреть' : _props$confirmText,
    _props$dismissText = props.dismissText,
    dismissText = _props$dismissText === void 0 ? 'Пропустить' : _props$dismissText,
    progressConfig = props.progressConfig,
    dataTestId = props.dataTestId;
  var toastApi = toastsApiContext.apiByPlacement[placement];
  React.useEffect(function () {
    var classToAdd = hidden ? styles['hidden'] : styles['visible'];
    var classToRemove = hidden ? styles['visible'] : styles['hidden'];

    /**
     * Не убирать таймаут, важно для корректной работы анимации.
     */
    setTimeout(function () {
      var _ref$current, _ref$current2;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.classList.add(classToAdd);
      (_ref$current2 = ref.current) === null || _ref$current2 === void 0 || _ref$current2.classList.remove(classToRemove);
    }, 0);
  }, [hidden]);
  var onTransitionEnd = React.useCallback(function (e) {
    if (hidden) {
      e.currentTarget.style.setProperty('display', 'none');
    }
  }, [hidden]);

  /**
   * При ховере на тост останавливаем его сокрытие, чтобы пользователь мог взаимодействовать
   * с его контентом столько, сколько требуется. Например, выделить и скопировать текст тоста.
   */
  var onMouseEnter = React.useCallback(function () {
    clearTimeout(currentHideTimerId);
  }, [currentHideTimerId]);

  /**
   * Убираем курсор с тоста - снова инициируем сокрытие по таймауту.
   */
  var onMouseLeave = React.useCallback(function () {
    if (isHidable) {
      var timerId = toastApi === null || toastApi === void 0 ? void 0 : toastApi.hideWithDelay(id, hideDelay);
      setCurrentHideTimerId(timerId);
    }
  }, [toastApi, hideDelay, id, isHidable]);
  var internalLeftAdornment = function () {
    /**
     * Если хочется кастомизации левой части тоста, то передаётся пропса leftAdornment, работает это ветвление.
     * Не запрещаем передавать null.
     * В таком случае LeftAdornment будет display: none; (см. CSS компонента LeftAdornment)
     */
    if (leftAdornmentRenderFunctionOrJSX !== undefined) {
      return /*#__PURE__*/_jsx(LeftAdornment, {
        children: typeof leftAdornmentRenderFunctionOrJSX === 'function' ? leftAdornmentRenderFunctionOrJSX({
          type: type,
          id: id,
          toastApi: toastApi
        }) : leftAdornmentRenderFunctionOrJSX
      });
    }
    var content = function () {
      switch (type) {
        case 'error':
          {
            return /*#__PURE__*/_jsx(LeftAdornment, {
              children: /*#__PURE__*/_jsx("span", {
                className: clsx(styles['icon-wrapper'], isMobileDevice && styles['mobile']),
                children: /*#__PURE__*/_jsx(IconFunctionalWarningCircle24, {
                  color: "icon-negative-default"
                })
              })
            });
          }
        case 'success':
          {
            return /*#__PURE__*/_jsx(LeftAdornment, {
              children: /*#__PURE__*/_jsx("span", {
                className: clsx(styles['icon-wrapper'], isMobileDevice && styles['mobile']),
                children: /*#__PURE__*/_jsx(IconFunctionalSuccessCircle24, {
                  color: "icon-positive-default"
                })
              })
            });
          }
        case 'info':
          return null;
        case 'spinner':
          {
            var spinnerSize = isMobileDevice ? 16 : 24;
            return /*#__PURE__*/_jsx(LeftAdornment, {
              children: /*#__PURE__*/_jsx(Spinner, {
                size: spinnerSize
              })
            });
          }
        case 'progress':
          return (progressConfig === null || progressConfig === void 0 ? void 0 : progressConfig.progressPercent) !== undefined && /*#__PURE__*/_jsx(LeftAdornment, {
            children: /*#__PURE__*/_jsx(ProgressBar, {
              progress: progressConfig.progressPercent
            })
          });
        default:
          return null;
      }
    }();
    return content;
  }();
  var internalRightAdornment = function () {
    /**
     * Если хочется кастомизации правой части тоста, то передаётся пропса rightAdornment, работает это ветвление.
     * Не запрещаем передавать null.
     * В таком случае RightAdornment будет display: none; (см. CSS компонента RightAdornment)
     */
    if (rightAdornmentRenderFunctionOrJSX !== undefined) {
      return /*#__PURE__*/_jsx(RightAdornment, {
        children: typeof rightAdornmentRenderFunctionOrJSX === 'function' ? rightAdornmentRenderFunctionOrJSX({
          type: type,
          id: id,
          toastApi: toastApi
        }) : rightAdornmentRenderFunctionOrJSX
      });
    }
    var isActionButtonsBlockVisible = confirmAction || dismissAction;
    if (isActionButtonsBlockVisible) {
      var hasBothControls = !!(confirmAction && dismissAction);
      var TypographyComponent = isMobileDevice ? UIText2 : ArticleParagraph1;
      return /*#__PURE__*/_jsxs(RightAdornment, {
        hasButtons: true,
        hasBothControls: hasBothControls,
        children: [(progressConfig === null || progressConfig === void 0 ? void 0 : progressConfig.progressText) && /*#__PURE__*/_jsx(ProgressAdornment, {
          progress: progressConfig.progressText
        }), confirmAction && /*#__PURE__*/_jsx("div", {
          className: clsx(styles['action-button'], styles['confirm'], !hasBothControls && styles['without-borders']),
          onClick: confirmAction,
          children: /*#__PURE__*/_jsx("div", {
            className: styles['action-text-wrapper'],
            children: /*#__PURE__*/_jsx(TypographyComponent, {
              color: "current_color",
              children: confirmText
            })
          })
        }), dismissAction && /*#__PURE__*/_jsx("div", {
          className: clsx(styles['action-button'], styles['dismiss'], !hasBothControls && styles['without-borders']),
          onClick: dismissAction,
          children: /*#__PURE__*/_jsx("div", {
            className: styles['action-text-wrapper'],
            children: /*#__PURE__*/_jsx(TypographyComponent, {
              color: "current_color",
              children: dismissText
            })
          })
        })]
      });
    }
    var hide = function hide() {
      onClose === null || onClose === void 0 || onClose();
      toastApi === null || toastApi === void 0 || toastApi.hide(id);
    };
    var content = function () {
      switch (type) {
        /* Для спиннера по умолчанию нет крестика закрытия */
        case 'spinner':
          return null;
        case 'error':
        case 'success':
        case 'info':
          {
            if (!hasCloseIcon) {
              return null;
            }
            var IconComponent = isMobileDevice ? IconActionClose16 : IconActionClose24;
            return /*#__PURE__*/_jsx(RightAdornment, {
              children: /*#__PURE__*/_jsx(IconComponent, {
                onClick: hide,
                cursor: "pointer"
              })
            });
          }
        case 'progress':
          {
            var _IconComponent = isMobileDevice ? IconActionClose16 : IconActionClose24;
            return /*#__PURE__*/_jsxs(RightAdornment, {
              children: [(progressConfig === null || progressConfig === void 0 ? void 0 : progressConfig.progressText) && /*#__PURE__*/_jsx(ProgressAdornment, {
                progress: progressConfig.progressText
              }), /*#__PURE__*/_jsx("div", {
                className: clsx(styles['progress-close'], isMobileDevice && styles['mobile']),
                children: /*#__PURE__*/_jsx(_IconComponent, {
                  onClick: hide,
                  cursor: "pointer"
                })
              })]
            });
          }
        default:
          return null;
      }
    }();
    return content;
  }();
  var content = function () {
    /**
     * Если хочется кастомизации основного контент-блока, то передаётся пропса content, работает это ветвление.
     * Не запрещаем передавать null. В таком случае селектор content будет display: none;
     */
    if (contentRenderFunctionOrJSX !== undefined) {
      return /*#__PURE__*/_jsx("section", {
        className: clsx(styles['content'], isMobileDevice && styles['mobile'], !internalLeftAdornment && styles['extended-left-padding']),
        children: typeof contentRenderFunctionOrJSX === 'function' ? contentRenderFunctionOrJSX({
          type: type,
          id: id,
          toastApi: toastApi
        }) : contentRenderFunctionOrJSX
      });
    }
    if (text) {
      var TypographyComponent = isMobileDevice ? UIText2 : ArticleParagraph1;
      return /*#__PURE__*/_jsx("section", {
        className: clsx(styles['text-content'], isMobileDevice && styles['mobile'], !internalLeftAdornment && styles['extended-left-padding']),
        children: /*#__PURE__*/_jsx(TypographyComponent, {
          color: "text-inverted-default",
          children: text
        })
      });
    }
    return null;
  }();
  return /*#__PURE__*/_jsxs("div", {
    className: clsx(styles['toast'], styles[placement], styles[type], styles[size], isMobileDevice && styles['mobile']),
    ref: ref,
    "data-type": type,
    "data-id": id,
    "data-testid": dataTestId,
    onTransitionEnd: onTransitionEnd,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    children: [internalLeftAdornment, content, internalRightAdornment]
  });
};
export var Toast = /*#__PURE__*/React.memo(ToastComponent);